import { useWebSocket } from '../contexts/WebSocketProvider';
import { Stack, Heading, Text, Box, Columns, Column, Button, IconDownload, Alert, IconTick } from 'braid-design-system';
import React from 'react';
import { WebSocketResponse } from '../schemas/webSocketResponseSchema';
import { usePageHeading } from '../contexts/PageHeadingProvider';
import SystemHealthCard from '../components/SystemHealthCard';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { useTranslation } from 'react-i18next';

const Dashboard = () => {
    const { wsClient } = useWebSocket();
    const { setPageHeading } = usePageHeading()
    const { isInstalled, hasNativePrompt, canInstall, installStatus, install } = usePWAInstall();

    const { t } = useTranslation();

    const installButtonLabel = () => {
        switch (installStatus) {
            case 'waiting':
                return t('page.dashboard.install.preparing', 'Preparing...');
            case 'prompting':
                return t('page.dashboard.install.installing', 'Installing...');
            case 'dismissed':
                return t('page.dashboard.install.dismissed', 'Dismissed');
            default:
                return hasNativePrompt ? t('page.dashboard.install.install_btn.install', 'Install App') : t('page.dashboard.install.install_btn.download', 'Download App');
        }
    };

    React.useEffect(() => {
        setPageHeading(t('page.dashboard.page_heading', 'Dashboard'));
        if (!wsClient) return;  // Wait until wsClient is initialized

        const handleMessage = (message: WebSocketResponse<any>) => {
            // eslint-disable-next-line no-console
            console.log('Received WebSocket message on WelcomePage:', message);
        };
        wsClient.onMessage(handleMessage);

        return () => {
            wsClient.offMessage(handleMessage);
        }
    }, [wsClient, setPageHeading]);


    return (
        <Box paddingTop={['small', 'small', 'medium', 'large']}>
            <Stack space="small">
                <Heading level="2">{t('page.dashboard.heading', 'Welcome to the Dashboard')}</Heading>
                <Text>{t('page.dashboard.description', 'Here you can find an overview of your account and recent activities.')}</Text>

                {isInstalled && (
                    <Alert tone="positive">
                        <Text>
                            <IconTick /> {t('page.dashboard.installed_alert', 'AIB Bank is installed as a desktop application.')}
                        </Text>
                    </Alert>
                )}

                {canInstall && (
                    <Box
                        background="promoteLight"
                        padding="medium"
                        borderRadius="large"
                    >
                        <Columns space="small" alignY="center">
                            <Column>
                                <Stack space="xsmall">
                                    <Text weight="strong">{t('page.dashboard.get_desktop.title', 'Get AIB Bank Desktop App')}</Text>
                                    <Text size="small" tone="secondary">
                                        {hasNativePrompt
                                            ? t('page.dashboard.get_desktop.description.install', 'Install for faster access, offline support, and a native desktop experience.')
                                            : t('page.dashboard.get_desktop.description.download', 'Download a shortcut installer to run AIB Bank as a desktop application.')}
                                    </Text>
                                </Stack>
                            </Column>
                            <Column width="content">
                                <Button
                                    icon={<IconDownload />}
                                    onClick={install}
                                    loading={installStatus === 'waiting' || installStatus === 'prompting'}
                                >
                                    {installButtonLabel()}
                                </Button>
                            </Column>
                        </Columns>
                    </Box>
                )}

                <Columns space="large" collapseBelow='tablet'>
                    <Column width='1/3'>
                        <SystemHealthCard />
                    </Column>
                </Columns>
            </Stack>
        </Box>
    );
};

export default Dashboard;
